import React, { useMemo, useState } from 'react';
import type { CcPriority, CcSuggestion } from '../../growth-campaign-create/types';
import { CC_SUGGESTIONS } from '../../growth-campaign-create/mockData';
import {
  CC_SERVICE_OPTIONS,
  GROWTH_CAMPAIGN_CREATE_META,
} from '../../growth-campaign-create/config';
import { Icon } from '../Icon/Icon';
import './growth-campaign-create.css';

export interface CreateCampaignViewProps {
  items?: CcSuggestion[];
  loading?: boolean;
  /** Called when the user approves a suggestion to launch. Parent wires routing to the preview page. */
  onPreview?: (suggestionId: number) => void;
  onDismiss?: (suggestionId: number) => void;
  /** Custom builder → `POST /campaigns/custom/` with live field names. */
  onGenerateCustom?: (input: {
    serviceCategory: string;
    monthlyBudgetCents: number;
    instructions?: string;
  }) => void;
}

const eyebrowClass =
  'text-[10px] font-bold uppercase tracking-[0.07em] text-[var(--ink-3)]';

const PRIORITY_TONE: Record<CcPriority, { accent: string; tint: string }> = {
  high: {
    accent: 'var(--terracotta)',
    tint: 'var(--terracotta-tint)',
  },
  medium: { accent: 'var(--gold)', tint: 'var(--gold-tint)' },
};

function CcCard({
  children,
  className = '',
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  return (
    <div
      className={[
        'rounded-[var(--r-xl)] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-1)]',
        className,
      ].join(' ')}
      style={style}
    >
      {children}
    </div>
  );
}

function CcPriorityBadge({ priority, suffix = '' }: { priority: CcPriority; suffix?: string }) {
  const t = PRIORITY_TONE[priority];
  return (
    <span
      className="inline-flex items-center rounded-[var(--r-pill)] px-2.5 py-1 text-[11px] font-semibold capitalize"
      style={{ background: t.tint, color: t.accent }}
    >
      {priority}
      {suffix}
    </span>
  );
}

function CcFeedItem({
  suggestion,
  active,
  onSelect,
}: {
  suggestion: CcSuggestion;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className="w-full cursor-pointer rounded-[var(--r-lg)] bg-[var(--cream-1)] px-[18px] py-4 text-left transition"
      style={{
        border: active
          ? '2px solid color-mix(in srgb, var(--success) 55%, transparent)'
          : '1px solid var(--cream-2)',
        boxShadow: active ? 'var(--sh-2)' : 'none',
      }}
    >
      <div className="mb-2 flex items-center gap-2.5">
        <span className="text-[22px] leading-none">{suggestion.icon}</span>
        <div className="min-w-0 flex-1">
          <div className="text-[15px] font-semibold text-[var(--ink-0)]">{suggestion.service}</div>
          <div className="mt-1">
            <CcPriorityBadge priority={suggestion.priority} />
          </div>
        </div>
      </div>
      <div className="mt-2 flex items-end justify-between">
        <div>
          <div className={eyebrowClass}>Spend</div>
          <div className="font-[var(--font-display)] text-[16px] font-bold text-[var(--ink-1)]">
            ${suggestion.spend}/mo
          </div>
        </div>
        <div className="text-center">
          <div className={eyebrowClass}>Patients</div>
          <div className="font-[var(--font-display)] text-[16px] font-bold text-[var(--success)]">
            {suggestion.patients}
          </div>
        </div>
        <div className="text-right">
          <div className={eyebrowClass}>ROI</div>
          <div className="font-[var(--font-display)] text-[16px] font-bold text-[var(--success)]">
            {suggestion.roi}
          </div>
        </div>
      </div>
    </button>
  );
}

function CcCustomBuilder({
  onClose,
  onGenerate,
  generating = false,
}: {
  onClose: () => void;
  onGenerate?: (input: {
    serviceCategory: string;
    monthlyBudgetCents: number;
    instructions?: string;
  }) => void;
  generating?: boolean;
}) {
  const [service, setService] = useState('emergency');
  const [budget, setBudget] = useState('500');
  const [notes, setNotes] = useState('');

  const fieldClass =
    'w-full rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-0)] px-3.5 py-2.5 font-[var(--font-body)] text-[14px] text-[var(--ink-1)] outline-none';

  const handleGenerate = () => {
    const dollars = Number.parseFloat(budget.replace(/[$,\s]/g, ''));
    const monthlyBudgetCents = Number.isFinite(dollars)
      ? Math.round(dollars * 100)
      : 50_000;
    onGenerate?.({
      serviceCategory: service || 'general-dentistry',
      monthlyBudgetCents: Math.min(5_000_000, Math.max(50_000, monthlyBudgetCents)),
      ...(notes.trim() ? { instructions: notes.trim() } : {}),
    });
  };

  return (
    <CcCard
      className="mb-7 p-6"
      style={{ borderColor: 'color-mix(in srgb, var(--success) 40%, transparent)' }}
    >
      <div className="mb-4 flex items-center gap-2">
        <Icon kind="plus" size={16} stroke={2.2} className="text-[var(--success)]" />
        <span className="font-[var(--font-display)] text-[17px] font-semibold text-[var(--ink-0)]">
          Create a Custom Campaign
        </span>
      </div>
      <p className="mb-5 text-[13px] leading-[1.6] text-[var(--ink-3)]">
        Tell ELVA what you want to advertise. It will generate a complete campaign with ads,
        keywords, and a landing page for your review.
      </p>
      <div className="cc-builder-grid mb-4 grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--ink-2)]">
            What service do you want to promote?
          </label>
          <select
            value={service}
            onChange={(e) => setService(e.target.value)}
            className={fieldClass}
          >
            {CC_SERVICE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1.5 block text-[13px] font-medium text-[var(--ink-2)]">
            Monthly budget
          </label>
          <input
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="e.g. $300"
            className={fieldClass}
          />
        </div>
      </div>
      <div className="mb-4">
        <label className="mb-1.5 block text-[13px] font-medium text-[var(--ink-2)]">
          Any specific instructions? (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="e.g. Focus on weekend appointments only, target Spanish-speaking patients, emphasize sedation options…"
          className={`${fieldClass} resize-y`}
        />
      </div>
      <div className="flex gap-2.5">
        <button
          type="button"
          disabled={generating}
          onClick={handleGenerate}
          className="cursor-pointer rounded-[var(--r-md)] border-0 px-6 py-2.5 text-[14px] font-semibold text-[var(--cream-1)] disabled:cursor-not-allowed disabled:opacity-60"
          style={{ background: 'var(--success)' }}
        >
          {generating ? 'Generating…' : 'Generate Campaign →'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2.5 text-[14px] font-semibold text-[var(--ink-2)]"
        >
          Cancel
        </button>
      </div>
    </CcCard>
  );
}

function CcDetailPanel({
  suggestion,
  onDismiss,
  onLaunch,
}: {
  suggestion: CcSuggestion;
  onDismiss: () => void;
  onLaunch: () => void;
}) {
  const metrics: { label: string; value: string; tone: string }[] = [
    { label: 'Monthly Spend', value: `$${suggestion.spend}`, tone: 'var(--ink-0)' },
    { label: 'Expected Patients', value: suggestion.patients, tone: 'var(--success)' },
    { label: 'Cost Per Patient', value: suggestion.costPerPatient, tone: 'var(--ink-1)' },
    {
      label: 'Avg Case Value',
      value: `$${suggestion.avgCaseValue.toLocaleString()}`,
      tone: 'var(--ink-0)',
    },
    { label: 'Return on Spend', value: suggestion.roi, tone: 'var(--success)' },
  ];

  return (
    <div className="flex flex-col gap-4">
      <CcCard className="px-7 py-6">
        <div className="mb-2 flex items-center gap-2.5">
          <span className="text-[28px] leading-none">{suggestion.icon}</span>
          <h2 className="m-0 font-[var(--font-display)] text-[22px] font-semibold tracking-[-0.02em] text-[var(--ink-0)]">
            {suggestion.service} Campaign
          </h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <CcPriorityBadge priority={suggestion.priority} suffix=" priority" />
          <span className="inline-flex rounded-[var(--r-pill)] bg-[var(--success-tint)] px-2.5 py-1 text-[11px] font-semibold text-[var(--success)]">
            {suggestion.confidence}% confidence
          </span>
          <span className="inline-flex rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11px] font-semibold text-[var(--ink-3)]">
            {suggestion.radiusMiles} mile radius
          </span>
        </div>

        <div className="mt-5">
          <div className="mb-1.5 text-[13px] font-semibold text-[var(--success)]">
            Who this targets
          </div>
          <p className="mb-4 text-[14px] leading-[1.7] text-[var(--ink-2)]">{suggestion.who}</p>

          <div className="rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--gold)_30%,transparent)] bg-[var(--gold-tint)] p-4">
            <div className="mb-1.5 text-[13px] font-semibold text-[var(--gold)]">Why now</div>
            <p className="text-[14px] leading-[1.7] text-[var(--ink-2)]">{suggestion.whyNow}</p>
          </div>
        </div>
      </CcCard>

      <div className="cc-metrics-grid grid gap-2.5" style={{ gridTemplateColumns: 'repeat(5, minmax(0, 1fr))' }}>
        {metrics.map((m) => (
          <CcCard key={m.label} className="px-4 py-3.5">
            <div className={eyebrowClass}>{m.label}</div>
            <div
              className="mt-1 font-[var(--font-display)] text-[20px] font-bold tracking-[-0.02em]"
              style={{ color: m.tone }}
            >
              {m.value}
            </div>
          </CcCard>
        ))}
      </div>

      <div className="flex items-center gap-2.5 rounded-[var(--r-xl)] border border-[color-mix(in_srgb,var(--success)_26%,transparent)] bg-[var(--success-tint)] px-[18px] py-3">
        <Icon kind="dollar" size={18} stroke={2} className="shrink-0 text-[var(--success)]" />
        <span className="text-[13px] font-medium text-[var(--success)]">
          For every <strong className="text-[var(--ink-1)]">$1</strong> you spend, you can expect{' '}
          <strong className="text-[var(--ink-1)]">
            {suggestion.roiEstimateMin}x–{suggestion.roiEstimateMax}x
          </strong>{' '}
          back in revenue from new patient appointments.
        </span>
      </div>

      <div className="cc-preview-grid grid gap-4" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
        <CcCard className="p-5">
          <div className="mb-3.5 text-[13px] font-semibold text-[var(--ink-2)]">Google Ad Preview</div>
          <div className="rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--surface-mute)] p-[18px]">
            <div className="mb-1.5 text-[11px] text-[var(--ink-4)]">Sponsored · {suggestion.ad.url}</div>
            <div className="mb-1.5 font-[var(--font-display)] text-[17px] font-semibold leading-[1.3] text-[var(--info)]">
              {suggestion.ad.headline}
            </div>
            <p className="mb-3 text-[13px] leading-[1.6] text-[var(--ink-2)]">{suggestion.ad.description}</p>
            <div className="flex flex-wrap gap-2">
              {suggestion.ad.extensions.map((ext) => (
                <span
                  key={ext}
                  className="rounded-[6px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1 text-[12px] text-[var(--ink-3)]"
                >
                  {ext}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {suggestion.keywords.slice(0, 4).map((keyword) => (
              <span
                key={keyword}
                className="rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11px] font-medium text-[var(--ink-2)]"
              >
                {keyword}
              </span>
            ))}
            {suggestion.keywords.length > 4 ? (
              <span className="rounded-[var(--r-pill)] bg-[var(--surface-mute)] px-2.5 py-1 text-[11px] font-medium text-[var(--ink-2)]">
                +{suggestion.keywords.length - 4} more
              </span>
            ) : null}
          </div>
        </CcCard>

        <CcCard className="p-5">
          <div className="mb-3.5 text-[13px] font-semibold text-[var(--ink-2)]">
            Landing Page Includes
          </div>
          <div className="flex flex-col gap-2">
            {suggestion.landingHighlights.map((item) => (
              <div
                key={item}
                className="flex items-start gap-2.5 rounded-[var(--r-sm)] bg-[var(--surface-mute)] px-3 py-2"
              >
                <Icon
                  kind="check"
                  size={16}
                  stroke={2.5}
                  className="mt-0.5 shrink-0 text-[var(--success)]"
                />
                <span className="text-[13px] leading-[1.5] text-[var(--ink-2)]">{item}</span>
              </div>
            ))}
          </div>
          <button
            type="button"
            className="mt-3.5 w-full cursor-pointer rounded-[var(--r-md)] border border-[color-mix(in_srgb,var(--success)_40%,transparent)] bg-[var(--success-tint)] px-4 py-2.5 text-[13px] font-semibold text-[var(--success)]"
          >
            Preview Landing Page →
          </button>
        </CcCard>
      </div>

      <CcCard className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
        <div>
          <div className="text-[14px] font-semibold text-[var(--ink-1)]">
            Ready to launch this campaign?
          </div>
          <div className="mt-0.5 text-[12px] text-[var(--ink-4)]">
            ELVA will create the ads, set up the landing page, and start tracking appointments
            automatically.
          </div>
        </div>
        <div className="flex shrink-0 gap-2.5">
          <button
            type="button"
            onClick={onDismiss}
            className="cursor-pointer rounded-[var(--r-md)] border border-[var(--cream-2)] bg-[var(--cream-1)] px-4 py-2.5 text-[13px] font-semibold text-[var(--ink-3)]"
          >
            Not Now
          </button>
          <button
            type="button"
            onClick={onLaunch}
            className="inline-flex cursor-pointer items-center gap-2 rounded-[var(--r-md)] border-0 px-6 py-2.5 text-[14px] font-semibold text-[var(--cream-1)] shadow-[var(--sh-1)]"
            style={{ background: 'var(--success)' }}
          >
            <Icon kind="bolt" size={16} stroke={2.4} />
            Approve & Launch
          </button>
        </div>
      </CcCard>
    </div>
  );
}

export function CreateCampaignView({
  items: itemsProp,
  loading = false,
  onPreview,
  onDismiss,
  onGenerateCustom,
}: CreateCampaignViewProps = {}) {
  const meta = GROWTH_CAMPAIGN_CREATE_META;
  const controlled = itemsProp !== undefined;
  const sourceItems = controlled ? itemsProp : CC_SUGGESTIONS;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [dismissed, setDismissed] = useState<Set<number>>(() => new Set());
  const [launched, setLaunched] = useState<Set<number>>(() => new Set());
  const [showBuilder, setShowBuilder] = useState(false);

  const visible = useMemo(
    () =>
      controlled
        ? sourceItems
        : sourceItems.filter((s) => !dismissed.has(s.id) && !launched.has(s.id)),
    [controlled, dismissed, launched, sourceItems],
  );

  const current = visible[selectedIndex] ?? visible[0];

  const handleDismiss = (id: number) => {
    if (onDismiss) {
      onDismiss(id);
      return;
    }
    setDismissed((prev) => new Set(prev).add(id));
    setSelectedIndex((index) => Math.max(0, Math.min(index, visible.length - 2)));
  };

  const handleLaunch = (id: number) => {
    onPreview?.(id);
    setLaunched((prev) => new Set(prev).add(id));
    setSelectedIndex((index) => Math.max(0, Math.min(index, visible.length - 2)));
  };

  return (
    <div className="growth-campaign-create-root relative mx-auto w-full max-w-[1320px] px-[30px] pb-[90px] pt-[26px]">
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-[720px]">
          <div className="mb-2 flex items-center gap-2">
            <Icon kind="sparkle" size={13} stroke={2} className="text-[var(--ai-3)]" />
            <span className="font-[var(--font-mono)] text-[11px] font-semibold uppercase tracking-[0.15em] text-[var(--ai-3)]">
              {meta.eyebrow}
            </span>
          </div>
          <h1 className="m-0 font-[var(--font-display)] text-[clamp(26px,3vw,33px)] font-semibold leading-[1.08] tracking-[-0.035em] text-[var(--ink-0)]">
            {meta.titleLead} <span className="elva-spectrum">{meta.titleAccent}</span>
          </h1>
          <p className="mt-2.5 text-[13.5px] leading-[1.55] text-[var(--ink-2)]">
            ELVA identified {visible.length} campaigns that can bring new patients this month.
            Review and launch the ones you like.
            {loading ? ' · Updating…' : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowBuilder((open) => !open)}
          className="inline-flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-[var(--r-md)] px-4 text-sm font-semibold transition"
          style={{
            border: showBuilder
              ? '1px solid color-mix(in srgb, var(--success) 40%, transparent)'
              : '1px solid var(--cream-2)',
            background: showBuilder ? 'var(--success-tint)' : 'var(--cream-1)',
            color: showBuilder ? 'var(--success)' : 'var(--ink-2)',
          }}
        >
          <Icon kind="plus" size={16} stroke={2.2} />
          Create Custom Campaign
        </button>
      </div>

      {showBuilder ? (
        <CcCustomBuilder
          onClose={() => setShowBuilder(false)}
          onGenerate={(input) => {
            onGenerateCustom?.(input);
            setShowBuilder(false);
          }}
          generating={loading}
        />
      ) : null}

      {visible.length === 0 ? (
        <CcCard className="px-6 py-14 text-center">
          <div className="mb-3 text-[36px]">✨</div>
          <div className="font-[var(--font-display)] text-[20px] font-semibold text-[var(--ink-1)]">
            All caught up
          </div>
          <div className="mt-2 text-[14px] text-[var(--ink-3)]">
            You&apos;ve reviewed all suggestions. ELVA will generate new opportunities as market
            conditions change.
          </div>
        </CcCard>
      ) : (
        <div className="cc-layout grid gap-6" style={{ gridTemplateColumns: '300px 1fr' }}>
          <div className="flex flex-col gap-2">
            {visible.map((suggestion, index) => (
              <CcFeedItem
                key={suggestion.id}
                suggestion={suggestion}
                active={current?.id === suggestion.id}
                onSelect={() => setSelectedIndex(index)}
              />
            ))}
          </div>

          {current ? (
            <CcDetailPanel
              suggestion={current}
              onDismiss={() => handleDismiss(current.id)}
              onLaunch={() => handleLaunch(current.id)}
            />
          ) : null}
        </div>
      )}

      <div className="mt-7 flex flex-wrap items-center justify-between gap-2 border-t border-[var(--cream-2)] pt-4">
        <span className="text-[12px] text-[var(--ink-4)]">
          Suggestions based on market intelligence, competitor analysis, and your clinic data
        </span>
        <span className="text-[12px] text-[var(--ink-4)]">
          {launched.size} launched · {dismissed.size} dismissed
        </span>
      </div>
    </div>
  );
}
